import { useState } from 'react';
import { Check, Instagram, Mail, MapPin, Phone } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

const Contact = () => {
  const { toast } = useToast();
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    serviceType: 'reklam',
    clientType: 'foretag'
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      console.log("Submitting contact form with data:", formData);
      const { data, error } = await supabase.functions.invoke('send-contact-email', {
        body: formData,
      });

      if (error) {
        console.error("Function invocation error:", error);
        throw error;
      }

      console.log("Function response:", data);
      setIsSubmitting(false);
      setIsSubmitted(true);
      
      // Track conversion
      if (typeof window !== 'undefined' && (window as any).gtag_report_conversion) {
        (window as any).gtag_report_conversion();
      }
      
      toast({
        title: "Tack för din förfrågan!",
        description: "Vi återkommer till dig inom kort.",
        duration: 5000
      });

      // Reset form after 5 seconds
      setTimeout(() => {
        setFormData({
          name: '',
          email: '',
          phone: '',
          company: '',
          message: '',
          serviceType: 'reklam',
          clientType: 'foretag'
        });
        setIsSubmitted(false);
      }, 5000);

    } catch (error) {
      console.error('Error sending contact form:', error);
      setIsSubmitting(false);
      toast({
        title: "Något gick fel",
        description: "Kunde inte skicka formuläret. Försök igen senare.",
        variant: "destructive",
        duration: 5000
      });
    }
  };
  
  return (
    <section id="kontakt" className="py-24 bg-gray-50">
      <div className="container-section">
        <div className="text-center mb-16">
          <h2 className="section-title">Kontakta oss</h2>
          <p className="section-subtitle max-w-3xl mx-auto">
            Redo att ta dina berättelser till nästa nivå? Vi ser fram emot att höra från dig.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 max-w-6xl mx-auto">
          {/* Contact Information */}
          <div className="lg:col-span-2">
            <div className="bg-storystore-blue text-white rounded-lg p-8 h-full">
              <h3 className="text-2xl font-bold mb-6">Låt oss prata</h3>
              
              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="bg-white/10 p-3 rounded-full mr-4">
                    <Mail size={20} />
                  </div>
                  <div>
                    <p className="font-medium">E-post</p>
                    <a href="mailto:info@story-store.se" className="text-white/80 hover:text-white">info@story-store.se</a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/10 p-3 rounded-full mr-4">
                    <Phone size={20} />
                  </div>
                  <div>
                    <p className="font-medium">Telefon</p>
                    <a href="tel:+46103333302" className="text-white/80 hover:text-white">010-333 33 02</a>
                  </div>
                </div>
                
                <div className="flex items-start">
                  <div className="bg-white/10 p-3 rounded-full mr-4">
                    <MapPin size={20} />
                  </div>
                  <div>
                    <p className="font-medium">Adress</p>
                    <address className="text-white/80 not-italic">
                      Stormbyvägen 2-4<br />
                      163 55 Stockholm
                    </address>
                  </div>
                </div>
              </div>
              
              <div className="mt-12">
                <h4 className="text-xl font-medium mb-4">Följ oss</h4>
                <div className="flex space-x-4">
                  <a href="https://www.instagram.com/storystore.se?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-colors">
                    <span className="sr-only">Instagram</span>
                    <Instagram size={18} />
                  </a>
                  <a href="https://www.tiktok.com/@storystore.se?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer" className="bg-white/10 hover:bg-white/20 w-10 h-10 rounded-full flex items-center justify-center transition-colors">
                    <span className="sr-only">TikTok</span>
                    <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 12a4 4 0 1 0 0 8 4 4 0 0 0 0-8z"/>
                      <path d="M16 8v8"/>
                      <path d="M12 16v4"/>
                      <path d="M20 10c0-4-1-7-6-7v8"/>
                    </svg>
                  </a>
                </div>
              </div>
            </div>
          </div>
          
          {/* Contact Form */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-lg p-8">
              <h3 className="text-2xl font-bold mb-6 text-storystore-blue">Skicka en förfrågan</h3>
              
              {isSubmitted ? <div className="text-center py-8">
                  <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                    <Check size={32} className="text-green-600" />
                  </div>
                  <h4 className="text-xl font-bold text-storystore-blue mb-2">Tack för din förfrågan!</h4>
                  <p className="text-gray-600">
                    Vi återkommer till dig så snart som möjligt, vanligtvis inom 24 timmar.
                  </p>
                </div> : <form onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="name" className="block text-gray-700 font-medium mb-2">
                        Namn *
                      </label>
                      <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue" placeholder="Ditt namn" />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-gray-700 font-medium mb-2">
                        E-post *
                      </label>
                      <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue" placeholder="din.email@exempel.se" />
                    </div>
                    
                    <div>
                      <label htmlFor="phone" className="block text-gray-700 font-medium mb-2">
                        Telefon
                      </label>
                      <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue" placeholder="070-123 45 67" />
                    </div>
                    
                    <div>
                      <label htmlFor="company" className="block text-gray-700 font-medium mb-2">
                        Företag/Organisation
                      </label>
                      <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue" placeholder="Ditt företag" />
                    </div>
                    
                    <div>
                      <label htmlFor="clientType" className="block text-gray-700 font-medium mb-2">
                        Kundtyp
                      </label>
                      <select id="clientType" name="clientType" value={formData.clientType} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue bg-white">
                        <option value="foretag">Företag</option>
                        <option value="influencer">Influencer</option>
                        <option value="annat">Annat</option>
                      </select>
                    </div>
                    
                    <div>
                      <label htmlFor="serviceType" className="block text-gray-700 font-medium mb-2">
                        Tjänst du är intresserad av
                      </label>
                      <select id="serviceType" name="serviceType" value={formData.serviceType} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue bg-white">
                        <option value="reklam">Reklam & Marknadsföring</option>
                        <option value="content">Content & Sociala Medier</option>
                        <option value="film">Filmproduktion</option>
                        <option value="annat">Annat/Osäker</option>
                      </select>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-gray-700 font-medium mb-2">
                      Meddelande *
                    </label>
                    <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-storystore-blue" placeholder="Beskriv kort ditt projekt eller din förfrågan..."></textarea>
                  </div>
                  
                  <div className="flex justify-end">
                    <button type="submit" disabled={isSubmitting} className={`btn-primary flex items-center justify-center min-w-[150px] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}>
                      {isSubmitting ? <>
                          <span className="mr-2">Skickar...</span>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        </> : 'Skicka förfrågan'}
                    </button>
                  </div>
                </form>}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
